import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Container,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FlashOn as RapidIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../../../../context/AuthContext';
import UserAuthButton from '../../auth/UserAuthButton';
import AuthModal from '../../auth/AuthModal';
import { useRapidQuizSession } from '../../../hooks/useRapidQuiz';
import RapidQuizMain from './RapidQuizMain';
import { getGuestDisplayName } from '../../../../utils/guestDisplayUtils';

// Styled components
const WelcomeContainer = styled(Container)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  py: 4,
}));

const SessionCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#FF9800',
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(255, 152, 0, 0.1)',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return '#FFC107';
      case 'active': return '#4CAF50';
      case 'ended': return '#F44336';
      default: return '#9E9E9E';
    }
  };
  
  return {
    backgroundColor: `${getStatusColor(status)}20`,
    color: getStatusColor(status),
    border: `1px solid ${getStatusColor(status)}`,
    fontWeight: 600,
  };
});

const RapidQuizWelcome = ({ onBack, onJoinQuiz }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userParticipations, setUserParticipations] = useState({});
  const [leaderboards, setLeaderboards] = useState({});
  const [leaderboardLoading, setLeaderboardLoading] = useState({});
  const [expandedLeaderboards, setExpandedLeaderboards] = useState({});
  const [joinedSession, setJoinedSession] = useState(null);
  const [joinedParticipant, setJoinedParticipant] = useState(null);
  const [resumeInfoBySession, setResumeInfoBySession] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAuthenticated } = useAuthContext();

  // Use the rapid quiz session hook
  const {
    sessions,
    loading,
    error,
    fetchSessions,
    joinSession
  } = useRapidQuizSession();

  // Refresh sessions
  const refreshSessions = async () => {
    setRefreshing(true);
    await fetchSessions();
    setRefreshing(false);
  };

  // Join rapid quiz session
  const handleJoinQuiz = async (session) => {
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      setAuthModalOpen(true);
      return;
    }

    // Only allow registered and guest users
    if (user.user_type !== 'registered' && user.user_type !== 'guest') {
      enqueueSnackbar('Please register or login as guest to join rapid quiz', { variant: 'warning' });
      setAuthModalOpen(true);
      return;
    }

    try {
      // Generate or get device ID
      let deviceId = localStorage.getItem('device_id');
      if (!deviceId) {
        deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('device_id', deviceId);
      }
      
      const participantData = {
        device_id: deviceId,
        anonymous_id: `anon_${Date.now()}`,
        guest_name: user.user_type === 'guest' ? getGuestDisplayName(user) : null
      };

      const result = await joinSession(session.session_id, participantData);
      
      // Persist rapid session context
      try {
        localStorage.setItem('rapid_session_id', String(session.session_id));
        if (result?.participant_id) {
          localStorage.setItem('rapid_participant_id', String(result.participant_id));
        }
      } catch (_) {}
      
      // Update participation status
      setUserParticipations(prev => ({
        ...prev,
        [session.session_id]: true
      }));
      
      // Show appropriate message
      if (result.is_rejoin) {
        enqueueSnackbar('Welcome back! Continuing your rapid quiz session.', { variant: 'success' });
      } else {
        enqueueSnackbar('Successfully joined rapid quiz session!', { variant: 'success' });
      }
      
      // Set joined session and participant for the main flow
      setJoinedSession(session);
      setJoinedParticipant(result);
      
      if (onJoinQuiz) {
        onJoinQuiz(session, result);
      }
    } catch (err) {
      console.error('Join rapid quiz error:', err);
      enqueueSnackbar('Failed to join rapid quiz session', { variant: 'error' });
    }
  };


  
  // Fetch leaderboard for a session
  const fetchLeaderboard = async (sessionId) => {
    try {
      setLeaderboardLoading(prev => ({ ...prev, [sessionId]: true }));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/leaderboard`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      
      const data = await response.json();
      setLeaderboards(prev => ({ ...prev, [sessionId]: data.leaderboard || [] }));
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      enqueueSnackbar('Failed to fetch leaderboard', { variant: 'error' });
    } finally {
      setLeaderboardLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  // Toggle leaderboard expansion
  const toggleLeaderboard = (sessionId) => {
    setExpandedLeaderboards(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
    
    if (!expandedLeaderboards[sessionId] && !leaderboards[sessionId]) {
      fetchLeaderboard(sessionId);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  // Get session status
  const getSessionStatus = (session) => {
    if (session.status === 'inactive') return 'inactive';
    if (session.rapid_status === 'waiting') return 'waiting';
    if (session.rapid_status === 'active') return 'active';
    if (session.rapid_status === 'ended') return 'ended';
    return 'inactive';
  };

  // Load sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Detect resumable session from localStorage and validate via resume endpoint
  useEffect(() => {
    const storedSessionId = localStorage.getItem('rapid_session_id');
    const storedParticipantId = localStorage.getItem('rapid_participant_id');
    if (!storedSessionId || !storedParticipantId) return;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    (async () => {
      try {
        const resp = await fetch(`${baseUrl}/api/live-quiz/sessions/${storedSessionId}/resume?participant_id=${storedParticipantId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('user_token') || ''}`
          }
        });
        if (!resp.ok) return;
        const data = await resp.json();
        setResumeInfoBySession(prev => ({
          ...prev,
          [storedSessionId]: {
            canResume: true,
            participant_id: parseInt(storedParticipantId, 10),
            current_score: data.current_score || 0
          }
        }));
        // Mark participation so UI shows appropriate state
        setUserParticipations(prev => ({ ...prev, [storedSessionId]: true }));
      } catch (_) {}
    })();
  }, []);

  const handleContinue = async (session) => {
    try {
      const storedSessionId = localStorage.getItem('rapid_session_id');
      const storedParticipantId = localStorage.getItem('rapid_participant_id');
      if (!storedSessionId || !storedParticipantId || String(session.session_id) !== String(storedSessionId)) {
        enqueueSnackbar('No resumable session found for this quiz', { variant: 'warning' });
        return;
      }
      // Build minimal participant object for RapidQuizMain
      const participantObj = { participant_id: parseInt(storedParticipantId, 10) };
      setJoinedSession(session);
      setJoinedParticipant(participantObj);
    } catch (e) {
      enqueueSnackbar('Failed to continue session', { variant: 'error' });
    }
  };

  // If user has joined a session, show the main quiz flow
  if (joinedSession && joinedParticipant) {
    return (
      <RapidQuizMain
        session={joinedSession}
        participant={joinedParticipant}
        onBack={() => {
          setJoinedSession(null);
          setJoinedParticipant(null);
        }}
        onComplete={() => {
          setJoinedSession(null);
          setJoinedParticipant(null);
          if (onBack) onBack();
        }}
      />
    );
  }

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Rapid Quiz Sessions
          </Typography>
          <UserAuthButton />
        </Toolbar>
      </AppBar>

      <WelcomeContainer maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <RapidIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Rapid Quiz Mode
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Join fast-paced quiz sessions with admin-controlled question progression. Answer questions as they appear in real-time!
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshSessions}
            disabled={refreshing}
            sx={{
              borderColor: '#FF9800',
              color: '#FF9800',
              '&:hover': {
                borderColor: '#F57C00',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
              }
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Sessions'}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#FF9800' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && sessions.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No active rapid quiz sessions available. Check back later or ask an admin to start a session.
          </Alert>
        )}

        <Grid container spacing={3}>
          {sessions.map((session) => {
            const sessionStatus = getSessionStatus(session);
            const hasJoined = userParticipations[session.session_id];
            const leaderboard = leaderboards[session.session_id] || [];
            const isLeaderboardExpanded = expandedLeaderboards[session.session_id];
            const isLeaderboardLoading = leaderboardLoading[session.session_id];

            return (
              <Grid item xs={12} md={6} lg={4} key={session.session_id}>
                <SessionCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {session.quiz_name}
                      </Typography>
                      <StatusChip 
                        label={sessionStatus} 
                        status={sessionStatus}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                        Question: {session.current_question_index || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                        Participants: {session.participant_count || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                        Duration: {session.question_duration_seconds || 30}s per question
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                        Created: {formatDate(session.created_at)}
                      </Typography>
                    </Box>

                    {/* Leaderboard Preview */}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        size="small"
                        startIcon={<TrophyIcon />}
                        onClick={() => toggleLeaderboard(session.session_id)}
                        sx={{ color: '#FF9800', mb: 1 }}
                      >
                        {isLeaderboardExpanded ? 'Hide' : 'Show'} Leaderboard
                      </Button>
                      
                      {isLeaderboardExpanded && (
                        <Box sx={{ mt: 1 }}>
                          {isLeaderboardLoading ? (
                            <LinearProgress sx={{ color: '#FF9800' }} />
                          ) : (
                            <List dense sx={{ backgroundColor: '#121212', borderRadius: 1 }}>
                              {leaderboard.slice(0, 3).map((participant, index) => (
                                <ListItem key={participant.participant_id} sx={{ py: 0.5 }}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', mr: 1 }}>
                                    {index + 1}
                                  </Avatar>
                                  <ListItemText
                                    primary={`${participant.fname} ${participant.lname || ''}`}
                                    secondary={`${participant.current_score || 0} points`}
                                    primaryTypographyProps={{ fontSize: '0.875rem', color: '#fff' }}
                                    secondaryTypographyProps={{ fontSize: '0.75rem', color: '#B0B0B0' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                      )}
                    </Box>

                    {resumeInfoBySession[session.session_id]?.canResume ? (
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<PlayIcon />}
                        onClick={() => handleContinue(session)}
                        sx={{
                          py: 1.5,
                          fontWeight: 'bold'
                        }}
                      >
                        Continue (Score: {resumeInfoBySession[session.session_id].current_score || 0})
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="warning"
                        fullWidth
                        startIcon={<PlayIcon />}
                        onClick={() => handleJoinQuiz(session)}
                        disabled={sessionStatus === 'inactive' || sessionStatus === 'ended'}
                        sx={{
                          py: 1.5,
                          fontWeight: 'bold',
                          backgroundColor: hasJoined ? '#4CAF50' : '#FF9800',
                          '&:hover': {
                            backgroundColor: hasJoined ? '#45a049' : '#F57C00',
                          }
                        }}
                      >
                        {hasJoined ? 'Rejoin Session' : 'Join Rapid Quiz'}
                      </Button>
                    )}
                  </CardContent>
                </SessionCard>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Rapid Quiz sessions are controlled by administrators. Questions appear when the admin starts them.
          </Typography>
        </Box>
      </WelcomeContainer>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => setAuthModalOpen(false)}
      />
    </>
  );
};

export default RapidQuizWelcome;
